import {
  InvoiceData,
  InvoiceViewOptions,
  RegionTaxRate,
  SaveInvoiceResponse,
  TypeTaxAssignment
} from "../../types/invoices";
import { dmnIdentity } from "../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../utils/environment";
import { errorHandler, apiFetchNotOkHandler } from "../errorHandler";
import { getBuildVersion } from "../getDefaultHeaders";

/**
 * Retrieve an access token and the base API URL.
 * Centralises the repeated token + environment lookup used by every operation.
 */
const getApiConfig = async (): Promise<{
  token: string;
  baseUrl: string | undefined;
}> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  return { token, baseUrl: environmentVars?.COLLINS_API_BASE_URL };
};

/**
 * Get the latest invoice for a booking
 * @param bookingId - The booking ID
 * @returns Promise resolving to the latest invoice data or null
 */
export const getLatestInvoice = async (
  bookingId: string
): Promise<InvoiceData | null> => {
  const { token, baseUrl } = await getApiConfig();

  try {
    const response = await fetch(
      `${baseUrl}/bookings/${bookingId}/invoices/latest`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      apiFetchNotOkHandler(response, "Error fetching latest invoice");
      return null;
    }

    const data = await response.json();
    return data?.invoice ?? data ?? null;
  } catch (error) {
    errorHandler(error, "Error fetching latest invoice");
    return null;
  }
};

/**
 * Get previous invoice revisions for a booking
 * @param bookingId - The booking ID
 * @returns Promise resolving to an array of previous invoice revisions
 */
export const getPreviousRevisions = async (
  bookingId: string
): Promise<InvoiceData[]> => {
  const { token, baseUrl } = await getApiConfig();

  try {
    const response = await fetch(`${baseUrl}/bookings/${bookingId}/invoices`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error fetching invoice revisions");
      return [];
    }

    const data = await response.json();
    return data?.invoices ?? data ?? [];
  } catch (error) {
    errorHandler(error, "Error fetching invoice revisions");
    return [];
  }
};

/**
 * Save an invoice for a booking (creates or updates)
 * @param bookingId - The booking ID
 * @param invoiceBody - The invoice data to save
 * @returns Promise resolving to the save response
 */
export const saveInvoice = async (
  bookingId: string,
  invoiceBody: InvoiceData
): Promise<SaveInvoiceResponse | null> => {
  const { token, baseUrl } = await getApiConfig();

  try {
    const response = await fetch(`${baseUrl}/bookings/${bookingId}/invoices`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Build-Version": getBuildVersion()
      },
      body: JSON.stringify(invoiceBody)
    });

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error saving invoice");
      return null;
    }

    const data = await response.json();
    return {
      invoice: data?.invoice ?? data,
      invoice_tax_config: data?.invoice_tax_config ?? null,
      booking_last_updated: data?.booking_last_updated ?? null
    };
  } catch (error) {
    errorHandler(error, "Error saving invoice");
    return null;
  }
};

/**
 * Delete an invoice for a booking
 * @param bookingId - The booking ID
 * @param invoiceId - The invoice ID to delete
 * @returns Promise resolving to true if successful
 */
export const deleteInvoice = async (
  bookingId: string,
  invoiceId: string
): Promise<boolean> => {
  const { token, baseUrl } = await getApiConfig();

  try {
    const response = await fetch(
      `${baseUrl}/bookings/${bookingId}/invoices/${invoiceId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Build-Version": getBuildVersion()
        }
      }
    );

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error deleting invoice");
      return false;
    }

    return true;
  } catch (error) {
    errorHandler(error, "Error deleting invoice");
    return false;
  }
};

/**
 * Send an invoice via email to the customer
 * @param bookingId - The booking ID
 * @param invoiceId - The invoice ID to email
 * @returns Promise resolving to true if successful
 */
export const sendInvoiceEmail = async (
  bookingId: string,
  invoiceId: string
): Promise<boolean> => {
  const { token, baseUrl } = await getApiConfig();

  try {
    const response = await fetch(
      `${baseUrl}/bookings/${bookingId}/invoices/${invoiceId}/send-email`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Build-Version": getBuildVersion()
        }
      }
    );

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error sending invoice email");
      return false;
    }

    return true;
  } catch (error) {
    errorHandler(error, "Error sending invoice email");
    return false;
  }
};

/**
 * Generate an invoice via the API (server-side generation)
 * @param bookingId - The booking ID
 * @returns Promise resolving to the generated invoice data
 */
export const generateInvoiceViaApi = async (
  bookingId: string
): Promise<{ invoice?: InvoiceData; booking_last_updated?: string }> => {
  const { token, baseUrl } = await getApiConfig();

  const response = await fetch(
    `${baseUrl}/bookings/${bookingId}/invoices/generate`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Build-Version": getBuildVersion()
      }
    }
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    throw new Error(
      `Failed to generate invoice (${response.status}): ${errorText}`
    );
  }

  const data = await response.json();
  return {
    invoice: data?.invoice ?? data,
    booking_last_updated: data?.booking_last_updated ?? null
  };
};

/**
 * Get printable HTML for an invoice
 * @param bookingId - The booking ID
 * @param invoiceId - The invoice ID
 * @param options - View options for the invoice
 * @returns Promise resolving to the HTML string
 */
export const getInvoicePrintHtml = async (
  bookingId: string,
  invoiceId: string,
  options?: InvoiceViewOptions
): Promise<string | null> => {
  const { token, baseUrl } = await getApiConfig();

  const params = new URLSearchParams();
  if (options?.invoiceIsText) params.append("invoiceIsText", "1");
  if (options?.showAmountDue) params.append("showAmountDue", "1");
  if (options?.invoiceIsCopy) params.append("invoiceIsCopy", "1");
  if (options?.invoiceIsProForma) params.append("invoiceIsProForma", "1");

  const queryString = params.toString();
  const suffix = queryString ? `?${queryString}` : "";
  const url = `${baseUrl}/bookings/${bookingId}/invoices/${invoiceId}/print${suffix}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "text/html"
      },
      cache: "no-cache"
    });

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error fetching invoice print HTML");
      return null;
    }

    return await response.text();
  } catch (error) {
    errorHandler(error, "Error fetching invoice print HTML");
    return null;
  }
};

/**
 * Get the PDF download URL for an invoice
 * @param bookingId - The booking ID
 * @param invoiceId - The invoice ID
 * @param options - View options for the invoice
 * @returns The download URL string
 */
/**
 * Build query string for invoice view options using camelCase param names
 */
const buildInvoiceOptionsQueryString = (
  options?: InvoiceViewOptions
): string => {
  const params = new URLSearchParams();
  if (options?.invoiceIsText) params.append("invoiceIsText", "1");
  if (options?.showAmountDue) params.append("showAmountDue", "1");
  if (options?.invoiceIsCopy) params.append("invoiceIsCopy", "1");
  if (options?.invoiceIsProForma) params.append("invoiceIsProForma", "1");
  return params.toString();
};

export const getInvoicePdfDownloadUrl = (
  bookingId: string,
  invoiceId: string,
  options?: InvoiceViewOptions
): string => {
  const queryString = buildInvoiceOptionsQueryString(options);
  const suffix = queryString ? `?${queryString}` : "";
  return `/bookings/${bookingId}/invoices/${invoiceId}/download${suffix}`;
};

/**
 * Download an invoice as PDF with authentication
 * Fetches the PDF as a blob with Bearer token and triggers a download
 */
export const downloadInvoicePdf = async (
  bookingId: string,
  invoiceId: string,
  options?: InvoiceViewOptions
): Promise<boolean> => {
  const { token, baseUrl } = await getApiConfig();
  const downloadPath = getInvoicePdfDownloadUrl(bookingId, invoiceId, options);
  const url = `${baseUrl}${downloadPath}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/pdf"
      },
      cache: "no-cache"
    });

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error downloading invoice PDF");
      return false;
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `invoice-${invoiceId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return true;
  } catch (error) {
    errorHandler(error, "Error downloading invoice PDF");
    return false;
  }
};

/**
 * Download an invoice as CSV with authentication
 * Fetches the CSV as a blob with Bearer token and triggers a download
 */
export const downloadInvoiceCsv = async (
  bookingId: string,
  invoiceId: string,
  options?: InvoiceViewOptions
): Promise<boolean> => {
  const { token, baseUrl } = await getApiConfig();
  const queryString = buildInvoiceOptionsQueryString(options);
  const suffix = queryString ? `?${queryString}` : "";
  const url = `${baseUrl}/bookings/${bookingId}/invoices/${invoiceId}/download-csv${suffix}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "text/csv"
      },
      cache: "no-cache"
    });

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error downloading invoice CSV");
      return false;
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `invoice-${invoiceId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return true;
  } catch (error) {
    errorHandler(error, "Error downloading invoice CSV");
    return false;
  }
};

/**
 * Get type-tax-assignments for a venue group.
 * Returns the mapping of preorder types to their assigned tax rates.
 * Matches collins-ui's VenueGroupPreordersProvider.getTypeTaxAssignments.
 * @param venueGroupId - The venue group ID
 * @param taxRegionId - Optional tax region ID to filter assignments
 * @returns Promise resolving to an array of type tax assignments
 */
export const getTypeTaxAssignments = async (
  venueGroupId: string,
  taxRegionId?: string | null
): Promise<TypeTaxAssignment[]> => {
  const { token, baseUrl } = await getApiConfig();

  const taxRegionQuery = taxRegionId ? `?tax_region_id=${taxRegionId}` : "";

  try {
    const response = await fetch(
      `${baseUrl}/venue-groups/${venueGroupId}/type-tax-assignments${taxRegionQuery}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error fetching type tax assignments");
      return [];
    }

    const data = await response.json();
    return data?.data ?? [];
  } catch (error) {
    errorHandler(error, "Error fetching type tax assignments");
    return [];
  }
};

/**
 * Get preorder types (typesCache) for a venue group.
 * Returns the list of preorder types with their IDs and names,
 * used for resolving type headings, service charge type, and deposits type.
 * Matches collins-ui's VenueGroupPreordersProvider.getTypes.
 * @param venueGroupId - The venue group ID
 * @returns Promise resolving to an array of preorder types
 */
export const getPreorderTypes = async (
  venueGroupId: string
): Promise<Array<{ id: string; name: string; invoice_label?: string }>> => {
  const { token, baseUrl } = await getApiConfig();

  try {
    const response = await fetch(
      `${baseUrl}/venue-groups/${venueGroupId}/preorder-types`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error fetching preorder types");
      return [];
    }

    const data = await response.json();
    return data?.data ?? [];
  } catch (error) {
    errorHandler(error, "Error fetching preorder types");
    return [];
  }
};

/**
 * Get available tax rates for a tax region.
 * Matches collins-ui's VenuePreordersProvider.getTaxRegionAvailableRates.
 * These are the selectable tax rates shown in the invoice item dropdown.
 * @param venueGroupId - The venue group ID
 * @param taxRegionId - The tax region ID
 * @returns Promise resolving to an array of region tax rates
 */
export const getTaxRegionAvailableRates = async (
  venueGroupId: string,
  taxRegionId: string
): Promise<RegionTaxRate[]> => {
  const { token, baseUrl } = await getApiConfig();

  try {
    const response = await fetch(
      `${baseUrl}/venue-groups/${venueGroupId}/tax-regions/${taxRegionId}/available-rates`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );

    if (!response.ok) {
      apiFetchNotOkHandler(
        response,
        "Error fetching tax region available rates"
      );
      return [];
    }

    const data = await response.json();
    return data?.data?.data ?? data?.data ?? [];
  } catch (error) {
    errorHandler(error, "Error fetching tax region available rates");
    return [];
  }
};
